/**
 * 017-style structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/roles-permissions/users-table/index.tsx — the
 * same paged record-list frame `../audit/List.tsx` composes (`ListHeader`, `PagedTable`, the status
 * dropdown). This is the dropdown's "View All Activity" target.
 *
 * Only the data, columns and labels are ours. `GET /api/identity/me/notifications` is server-paged
 * and already filtered by the caller's live permissions, so no `can()` gate applies: every signed-in
 * account reaches this page and sees exactly what Identity says it may.
 */
import { useEffect, useState } from "react";
import { useLoaderData, useRevalidator, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import { Button } from "rizzui";
import toast from "react-hot-toast";
import { EmptyState, useNotifications, type NotificationItem } from "@steelyard/theme";
import { PagedTable } from "../../components/table/data-table";
import { ListHeader } from "../../components/table/list-header";
import { notificationColumns } from "./columns";
import { listNotifications } from "../../lib/services/notifications";
import { hrefWith, readPaging } from "../../lib/paging";
import { enablePush, pushState, type PushState } from "../../lib/push";

export async function notificationsLoader({ request }: LoaderFunctionArgs) {
  const params = new URL(request.url).searchParams;
  const { page, pageSize } = readPaging(params);
  const unreadOnly = params.get("filter") === "unread";
  return { result: await listNotifications({ page, pageSize, unreadOnly }), unreadOnly };
}

export default function NotificationsList() {
  const { result, unreadOnly } = useLoaderData() as Awaited<ReturnType<typeof notificationsLoader>>;
  const { markRead, markAllRead } = useNotifications();
  const revalidator = useRevalidator();
  const [searchParams] = useSearchParams();
  const [push, setPush] = useState<PushState>("unsupported");

  useEffect(() => {
    void pushState().then(setPush);
  }, []);

  // The browser's permission prompt, from a click as Safari and Firefox require.
  const turnOnPush = async () => {
    try {
      const state = await enablePush(true);
      setPush(state);
      if (state === "granted") toast.success("Browser notifications are on for this browser.");
      if (state === "denied") toast.error("Notifications are blocked. Allow them in this site's browser settings.");
    } catch {
      toast.error("Browser notifications could not be turned on. Try again.");
    }
  };

  const open = (item: NotificationItem) => {
    if (!item.read) void markRead(item.id).then(() => revalidator.revalidate());
  };

  const status = {
    label: "Show",
    options: [
      { label: "All", href: hrefWith("/notifications", searchParams, { filter: undefined, page: undefined }), active: !unreadOnly },
      { label: `Unread (${result.unreadCount})`, href: hrefWith("/notifications", searchParams, { filter: "unread", page: undefined }), active: unreadOnly },
    ],
  };

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="Notifications"
        count={result.totalCount}
        description="What needs attention across orders, customers, pricing, packing, invoicing and Tally — only what your permissions reach."
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Notifications" }]}
      />

      <PagedTable
        actions={
          <>
            {push === "default" ? (
              <Button variant="outline" onClick={() => void turnOnPush()}>
                Enable browser notifications
              </Button>
            ) : null}
            {push === "denied" ? (
              <Button variant="outline" disabled title="Allow notifications for this site in your browser's site settings.">
                Browser notifications blocked
              </Button>
            ) : null}
            <Button
              variant="outline"
              disabled={result.unreadCount === 0}
              onClick={() => void markAllRead().then(() => revalidator.revalidate())}
            >
              Mark all as read
            </Button>
          </>
        }
        result={result}
        columns={notificationColumns(open)}
        getRowId={(row) => row.id}
        caption="Notifications: severity, what happened, when, and whether you have read it"
        status={status}
        clear={{ active: unreadOnly, href: hrefWith("/notifications", searchParams, { filter: undefined, page: undefined }) }}
        empty={
          <EmptyState title={unreadOnly ? "Nothing unread" : "No notifications"}>
            {unreadOnly ? "You have read everything." : "Nothing has needed your attention yet."}
          </EmptyState>
        }
      />
    </div>
  );
}
